import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Button,
  Typography,
  Autocomplete
} from '@mui/material';
import { Search, Clear, FileDownload } from '@mui/icons-material';
import Select from 'react-select';
import { getAuthToken } from '../../utils/cookieUtils';

const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

// --- Types ---

interface LineageFacility {
  id: string;
  name: string;
}

interface Lineage {
  uuid: string;
  facility_id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  last_moved_at: string;
  current_yard_spot_uuid: string | null;
  current_dock_uuid: string | null;
  trailer: { uuid: string; trailer_number: string } | null;
  facility: LineageFacility | null;
}

interface SearchResult {
  trailer_number: string;
  scac: string;
  total_count: number;
  lineages: Lineage[];
}

interface Facility {
  id: string;
  name: string;
  configuration?: string | Record<string, any> | null;
}

// --- Helpers ---

const formatDateTime = (dateString: string, timezone?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const tz = timezone || 'UTC';
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    const parts = formatter.formatToParts(date);
    const p: Record<string, string> = {};
    parts.forEach(part => { p[part.type] = part.value; });
    const tzAbbr = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short'
    }).formatToParts(date).find(part => part.type === 'timeZoneName')?.value || tz;
    return `${p.month}/${p.day}/${p.year} ${p.hour}:${p.minute} ${tzAbbr}`;
  } catch {
    return date.toLocaleString();
  }
};

const getFacilityTimezone = (facility: Facility): string => {
  if (!facility.configuration) return 'UTC';
  try {
    const config = typeof facility.configuration === 'string'
      ? JSON.parse(facility.configuration)
      : facility.configuration;
    return config.timezone || 'UTC';
  } catch {
    return 'UTC';
  }
};

// --- GraphQL calls ---

// facilityId '*' searches every campus of the user; with wholeCampus, that facility's campus.
async function searchLineages(trailerNumber: string, scac: string, facilityId: string, wholeCampus: boolean): Promise<SearchResult> {
  const authToken = getAuthToken();
  const query = `
    query SearchTrailerLineages($trailerNumber: String!, $scac: String!, $facilityId: String, $wholeCampus: Boolean) {
      search_trailer_lineages(trailer_number: $trailerNumber, scac: $scac, facility_id: $facilityId, whole_campus: $wholeCampus) {
        trailer_number
        scac
        total_count
        lineages {
          uuid
          facility_id
          is_active
          created_at
          updated_at
          last_moved_at
          current_yard_spot_uuid
          current_dock_uuid
          trailer {
            uuid
            trailer_number
          }
          facility {
            id
            name
          }
        }
      }
    }
  `;
  const res = await fetch(GRAPHQL_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
    },
    body: JSON.stringify({
      query,
      variables: { trailerNumber, scac, facilityId, wholeCampus }
    })
  });
  const json = await res.json();
  if (json.errors) throw new Error(json.errors[0].message);
  return json.data.search_trailer_lineages;
}

// One workbook for the whole campus of `facilityId`.
async function exportHistoryXlsx(facilityId: string, trailerNumber: string, scac: string) {
  const authToken = getAuthToken();
  const query = `
    mutation ExportTrailerHistory($facilityId: ID!, $trailerNumber: String!, $scac: String!) {
      export_trailer_history_xlsx(facility_id: $facilityId, trailer_number: $trailerNumber, scac: $scac, whole_campus: true) {
        as_xlsx {
          success
          message
          file_data
          filename
          record_count
        }
      }
    }
  `;
  const res = await fetch(GRAPHQL_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
    },
    body: JSON.stringify({
      query,
      variables: { facilityId, trailerNumber, scac }
    })
  });
  const json = await res.json();
  if (json.errors) throw new Error(json.errors[0].message);
  return json.data.export_trailer_history_xlsx.as_xlsx;
}

interface TrailerAutocompleteItem {
  trailerNumber: string;
  scac: string;
}

async function fetchTrailerAutocomplete(): Promise<TrailerAutocompleteItem[]> {
  const authToken = getAuthToken();
  const res = await fetch(GRAPHQL_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
    },
    body: JSON.stringify({
      query: `query { trailerAutocomplete { trailerNumber scac } }`
    })
  });
  const json = await res.json();
  if (json.errors) throw new Error(json.errors[0].message);
  return json.data.trailerAutocomplete || [];
}

interface Campus {
  /** Campus uuid, or `facility:<id>` for a facility with no campus. */
  key: string;
  name: string;
  /** The user's own facility in the campus; searches and exports are anchored on it. */
  anchorId: string;
  facilityIds: string[];
}

/**
 * The user's campuses and every facility in them (including ones the user is not
 * assigned to, whose history is still theirs to see).
 */
async function fetchCampuses(): Promise<{ campuses: Campus[]; facilities: Facility[] }> {
  const authToken = getAuthToken();
  const res = await fetch(GRAPHQL_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
    },
    body: JSON.stringify({
      query: `query { facilities { id name configuration campus { uuid name facilities { id name configuration } } } }`
    })
  });
  const json = await res.json();
  if (json.errors) throw new Error(json.errors[0].message);

  const facilitiesById = new Map<string, Facility>();
  const campusesByKey = new Map<string, Campus>();
  (json.data.facilities || []).forEach((f: any) => {
    const members: any[] = f.campus?.facilities?.length ? f.campus.facilities : [f];
    members.forEach((m: any) => {
      if (!facilitiesById.has(String(m.id))) {
        facilitiesById.set(String(m.id), { id: String(m.id), name: m.name, configuration: m.configuration });
      }
    });
    const key = f.campus?.uuid || `facility:${f.id}`;
    if (!campusesByKey.has(key)) {
      campusesByKey.set(key, {
        key,
        name: f.campus?.name || f.name,
        anchorId: String(f.id),
        facilityIds: members.map((m: any) => String(m.id))
      });
    }
  });
  return { campuses: Array.from(campusesByKey.values()), facilities: Array.from(facilitiesById.values()) };
}

// --- Component ---

type View = 'search' | 'lineages';

const TrailerHistoryTab: React.FC = () => {
  // Facility dropdown
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [selectedCampus, setSelectedCampus] = useState<Campus | null>(null);
  const [facilitiesLoading, setFacilitiesLoading] = useState(true);

  // Facility ID → timezone lookup
  const facilityTimezones = useMemo(() => {
    const map: Record<string, string> = {};
    facilities.forEach(f => { map[f.id] = getFacilityTimezone(f); });
    return map;
  }, [facilities]);

  // Autocomplete data
  const [autocompleteData, setAutocompleteData] = useState<TrailerAutocompleteItem[]>([]);

  // Search form
  const [trailerNumber, setTrailerNumber] = useState('');
  const [scac, setScac] = useState('');

  // State machine
  const [view, setView] = useState<View>('search');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Lineages result
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);

  // Export state — the campus key currently exporting (null = none)
  const [exportingCampusKey, setExportingCampusKey] = useState<string | null>(null);

  // Load facilities on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await fetchCampuses();
        if (cancelled) return;
        setFacilities(result.facilities);
        setCampuses(result.campuses);
      } catch (err: any) {
        if (!cancelled) setError('Failed to load facilities');
      } finally {
        if (!cancelled) setFacilitiesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load trailer autocomplete data on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await fetchTrailerAutocomplete();
        if (!cancelled) setAutocompleteData(result);
      } catch (err) {
        console.error('Failed to fetch trailer autocomplete data:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Unique sorted SCACs for autocomplete
  const autocompleteScacs = useMemo(() => {
    const scacs = new Set<string>();
    autocompleteData.forEach(item => {
      if (item.scac) scacs.add(item.scac);
    });
    return Array.from(scacs).sort();
  }, [autocompleteData]);

  // Trailer numbers filtered by current SCAC selection
  const autocompleteTrailerNumbers = useMemo(() => {
    if (!scac.trim()) return [];
    const scacUpper = scac.trim().toUpperCase();
    return autocompleteData
      .filter(item => item.scac.toUpperCase() === scacUpper)
      .map(item => item.trailerNumber)
      .sort();
  }, [autocompleteData, scac]);

  const handleSearch = useCallback(async () => {
    if (!trailerNumber.trim() || !scac.trim()) return;
    setLoading(true);
    setError(null);
    setSearchResult(null);
    try {
      const result = await searchLineages(
        trailerNumber.trim(),
        scac.trim().toUpperCase(),
        selectedCampus ? selectedCampus.anchorId : '*',
        !!selectedCampus
      );
      setSearchResult(result);
      setView('lineages');
    } catch (err: any) {
      setError(err.message || 'Search failed');
      setView('search');
    } finally {
      setLoading(false);
    }
  }, [trailerNumber, scac, selectedCampus]);

  const handleExport = useCallback(async (campus: Campus) => {
    if (!searchResult) return;
    setExportingCampusKey(campus.key);
    try {
      const result = await exportHistoryXlsx(
        campus.anchorId,
        searchResult.trailer_number,
        searchResult.scac
      );
      if (result.success && result.file_data) {
        const binaryString = atob(result.file_data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = result.filename || `trailer_history_${searchResult.trailer_number}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        setError(result.message || 'Export failed');
      }
    } catch (err: any) {
      setError(err.message || 'Export failed');
    } finally {
      setExportingCampusKey(null);
    }
  }, [searchResult]);

  // Lineages grouped by campus, then by facility (campuses in dropdown order).
  const lineagesByCampus = useMemo(() => {
    if (!searchResult) return [];
    type FacilityGroup = { facilityId: string; facilityName: string; lineages: Lineage[] };
    const campusOf = (facilityId: string) =>
      campuses.find(c => c.facilityIds.includes(facilityId)) ||
      { key: `facility:${facilityId}`, name: '', anchorId: facilityId, facilityIds: [facilityId] };
    const groups = new Map<string, { campus: Campus; facilities: Map<string, FacilityGroup> }>();
    searchResult.lineages.forEach(lineage => {
      const fid = String(lineage.facility?.id || lineage.facility_id);
      const fname = lineage.facility?.name || `Facility ${lineage.facility_id}`;
      const campus = campusOf(fid);
      if (!groups.has(campus.key)) {
        groups.set(campus.key, { campus: { ...campus, name: campus.name || fname }, facilities: new Map() });
      }
      const facilities = groups.get(campus.key)!.facilities;
      if (!facilities.has(fid)) facilities.set(fid, { facilityId: fid, facilityName: fname, lineages: [] });
      facilities.get(fid)!.lineages.push(lineage);
    });
    const order = (key: string) => {
      const i = campuses.findIndex(c => c.key === key);
      return i === -1 ? campuses.length : i;
    };
    return Array.from(groups.values())
      .sort((a, b) => order(a.campus.key) - order(b.campus.key))
      .map(g => ({ campus: g.campus, facilities: Array.from(g.facilities.values()) }));
  }, [searchResult, campuses]);

  const handleReset = () => {
    setView('search');
    setSearchResult(null);
    setError(null);
  };

  return (
    <div>
      {/* Search Bar */}
      <div className="bg-white p-4 shadow mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center">
            <Select
              options={[
                { value: '*', label: 'All Campuses' },
                ...campuses.map(c => ({ value: c.key, label: c.name }))
              ]}
              value={selectedCampus
                ? { value: selectedCampus.key, label: selectedCampus.name }
                : { value: '*', label: 'All Campuses' }
              }
              onChange={(option) => {
                setSelectedCampus(
                  !option || option.value === '*' ? null : campuses.find(c => c.key === option.value) || null
                );
              }}
              className="w-48"
              isSearchable
              isLoading={facilitiesLoading}
              placeholder="Select Campus..."
            />
          </div>

          <div className="h-8 w-px bg-gray-300 mx-2"></div>

          <Autocomplete
            freeSolo
            options={autocompleteScacs}
            inputValue={scac}
            onInputChange={(event, newValue) => {
              const upper = newValue.toUpperCase();
              setScac(upper);
              // Clear trailer number when SCAC changes so user picks from filtered list
              if (event) setTrailerNumber('');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="SCAC (Carrier Code)"
                size="small"
                placeholder="e.g. FEDX"
                onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                inputProps={{ ...params.inputProps, style: { textTransform: 'uppercase' } }}
              />
            )}
            sx={{ width: 200 }}
          />
          <Autocomplete
            freeSolo
            options={autocompleteTrailerNumbers}
            inputValue={trailerNumber}
            onInputChange={(event, newValue) => {
              setTrailerNumber(newValue.toUpperCase());
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Trailer Number"
                size="small"
                placeholder={scac.trim() ? 'e.g. 53-1234' : 'Select a SCAC first'}
                onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" sx={{ color: 'grey.500' }} />
                    </InputAdornment>
                  )
                }}
              />
            )}
            sx={{ width: 220 }}
          />
          <Button
            variant="contained"
            size="medium"
            onClick={handleSearch}
            disabled={!trailerNumber.trim() || !scac.trim() || loading}
            startIcon={loading ? <CircularProgress size={16} /> : <Search />}
          >
            {loading ? 'Searching...' : 'Search'}
          </Button>
          {view !== 'search' && (
            <Button
              variant="outlined"
              size="medium"
              onClick={handleReset}
              startIcon={<Clear />}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center items-center py-16">
          <CircularProgress />
        </div>
      )}

      {/* Lineages List */}
      {!loading && view === 'lineages' && searchResult && (
        <div>
          {searchResult.total_count === 0 && (
            <div className="bg-white shadow p-4">
              <Typography variant="h6">
                No records found for {searchResult.trailer_number} / {searchResult.scac}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Double-check the trailer number and SCAC code. SCAC codes are carrier short codes like FEDX, XPO, SNLU, etc.
              </Typography>
            </div>
          )}

          {lineagesByCampus.map(({ campus, facilities: facilityGroups }) => {
            const isExporting = exportingCampusKey === campus.key;
            const visits = facilityGroups.reduce((n, g) => n + g.lineages.length, 0);
            return (
              <div key={campus.key} className="mb-6">
                {/* Campus header: one export for every facility in the campus */}
                <div className="bg-white shadow mb-2 p-4 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <Typography variant="h5">{campus.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {visits} visit{visits !== 1 ? 's' : ''} across {facilityGroups.length} facilit
                      {facilityGroups.length !== 1 ? 'ies' : 'y'}
                    </Typography>
                  </div>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleExport(campus)}
                    disabled={exportingCampusKey !== null}
                    startIcon={isExporting ? <CircularProgress size={14} /> : <FileDownload />}
                  >
                    {isExporting ? 'Exporting...' : 'Export XLSX'}
                  </Button>
                </div>

                {facilityGroups.map(group => {
                  const tz = facilityTimezones[group.facilityId] || 'UTC';
                  return (
                    <div key={group.facilityId} className="bg-white shadow mb-4">
                      <div className="p-4 border-b">
                        <Typography variant="h6">{group.facilityName}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {group.lineages.length} visit{group.lineages.length !== 1 ? 's' : ''}
                        </Typography>
                      </div>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                              <TableCell><strong>Arrived</strong></TableCell>
                              <TableCell><strong>Last Moved</strong></TableCell>
                              <TableCell><strong>Last Updated</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {group.lineages.map(lineage => (
                              <TableRow key={lineage.uuid} hover>
                                <TableCell>{formatDateTime(lineage.created_at, tz)}</TableCell>
                                <TableCell>{lineage.last_moved_at ? formatDateTime(lineage.last_moved_at, tz) : '-'}</TableCell>
                                <TableCell>{formatDateTime(lineage.updated_at, tz)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {!loading && view === 'search' && !error && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
          <Search sx={{ fontSize: 48, color: 'grey.300', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Enter a trailer number and SCAC code to search for trailer history.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            SCAC is the carrier's short code (e.g. FEDX, XPO, SNLU).
          </Typography>
        </div>
      )}
    </div>
  );
};

export default TrailerHistoryTab;
